import { useState, useEffect, useMemo } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { CalendarDays, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { themaFeestThemas } from "../data/content";

const ARRANGEMENTEN = [
  { id: "basisfeest", label: "Basisfeest", hint: "vanaf 50 personen · 's avonds", min: 50 },
  { id: "themafeest", label: "Themafeest", hint: "vanaf 80 personen · incl. entertainment", min: 80 },
  { id: "buffet", label: "Buffetarrangement", hint: "vanaf 30 personen · overdag/vroege avond", min: 30 },
];

const DAGDELEN = [
  { id: "overdag", label: "Overdag / vroege avond (buffet, klaar om 20:00)" },
  { id: "avond", label: "Avond (feest)" },
];

function toISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function berekenPrijs(arrangement, n) {
  if (!n || n < 1) return null;
  if (arrangement === "basisfeest") {
    if (n < 50) return { error: "Het basisfeest is vanaf 50 personen." };
    const pp = n >= 100 ? 40 : 43.5;
    return { pp, totaal: pp * n };
  }
  if (arrangement === "themafeest") {
    if (n < 80) return { error: "Het themafeest is vanaf 80 personen." };
    const pp = n >= 120 ? 64.95 : 68.45;
    return { pp, totaal: pp * n };
  }
  if (arrangement === "buffet") {
    if (n < 30) return { error: "Het buffetarrangement is vanaf 30 personen." };
    return { pp: 69.5, totaal: 69.5 * n };
  }
  return null;
}

const euro = (v) => "€ " + v.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Reserveren() {
  const [form, setForm] = useState({
    arrangement: "basisfeest",
    dagdeel: "avond",
    aantal_personen: "",
    thema: "",
    naam: "",
    email: "",
    telefoon: "",
    bericht: "",
  });
  const [datum, setDatum] = useState(null);
  const [bezet, setBezet] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get("/bookings/availability").then(({ data }) => setBezet(data)).catch(() => {});
  }, []);

  const disabledDays = useMemo(() => {
    const taken = bezet
      .filter((b) => b.dagdeel === form.dagdeel)
      .map((b) => {
        const [y, m, d] = b.datum.split("-").map(Number);
        return new Date(y, m - 1, d);
      });
    return [{ before: new Date() }, ...taken];
  }, [bezet, form.dagdeel]);

  const aantal = parseInt(form.aantal_personen, 10);
  const prijs = berekenPrijs(form.arrangement, aantal);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!datum) return toast.error("Kies eerst een datum in de kalender.");
    if (!prijs || prijs.error) return toast.error(prijs?.error || "Vul een geldig aantal personen in.");
    setLoading(true);
    try {
      await client.post("/bookings", {
        naam: form.naam,
        email: form.email,
        telefoon: form.telefoon,
        datum: toISO(datum),
        dagdeel: form.dagdeel,
        arrangement: form.arrangement,
        aantal_personen: aantal,
        thema: form.arrangement === "themafeest" ? form.thema : null,
        bericht: form.bericht,
      });
      window.location.href = "/reserveren/bevestiging";
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Er ging iets mis.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="max-w-2xl mb-10">
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">Reserveren</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mt-3">
            Reserveer jouw datum
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Kies een arrangement, datum en aantal personen en verstuur je aanvraag. Je ontvangt daarna een offerte van Daniëlle; na akkoord is je reservering definitief. Liever even overleggen? <a href="/#contact" className="text-primary hover:underline">Neem contact op</a>.
          </p>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1.5">Arrangement</label>
              <select data-testid="booking-arrangement" value={form.arrangement} onChange={update("arrangement")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary">
                {ARRANGEMENTEN.map((a) => <option key={a.id} value={a.id}>{a.label} — {a.hint}</option>)}
              </select>
            </div>

            {form.arrangement === "themafeest" && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Thema</label>
                <select data-testid="booking-thema" value={form.thema} onChange={update("thema")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary">
                  <option value="">Maakt niet uit / in overleg</option>
                  {themaFeestThemas.map((t) => <option key={t.naam} value={t.naam}>{t.naam}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1.5">Dagdeel</label>
              <select data-testid="booking-dagdeel" value={form.dagdeel} onChange={update("dagdeel")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary">
                {DAGDELEN.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Aantal personen</label>
              <input data-testid="booking-aantal" type="number" min="1" required value={form.aantal_personen} onChange={update("aantal_personen")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" placeholder="bijv. 50" />
              {prijs?.error && <p className="text-sm text-destructive mt-1.5">{prijs.error}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="booking-naam" required value={form.naam} onChange={update("naam")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" placeholder="Naam" />
              <input data-testid="booking-telefoon" required value={form.telefoon} onChange={update("telefoon")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" placeholder="Telefoon" />
            </div>
            <input data-testid="booking-email" type="email" required value={form.email} onChange={update("email")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" placeholder="E-mailadres" />
            <textarea data-testid="booking-bericht" rows={3} value={form.bericht} onChange={update("bericht")} className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary resize-none" placeholder="Eventuele wensen of opmerkingen (optioneel)" />
          </div>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-5">
              <p className="flex items-center gap-2 text-sm font-medium mb-3"><CalendarDays size={18} className="text-primary" /> Kies een datum</p>
              <DayPicker
                mode="single"
                selected={datum}
                onSelect={setDatum}
                disabled={disabledDays}
                weekStartsOn={1}
                styles={{ caption: { color: "hsl(18 55% 45%)" } }}
                modifiersStyles={{ selected: { backgroundColor: "hsl(15 56% 50%)", color: "white" } }}
              />
              <p className="text-xs text-muted-foreground mt-2">Bezette datums voor het gekozen dagdeel zijn niet selecteerbaar.</p>
            </div>

            <div data-testid="booking-summary" className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-4">Overzicht</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between"><span className="text-muted-foreground">Datum</span><span className="font-medium">{datum ? toISO(datum) : "—"}</span></li>
                <li className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Users size={14} /> Personen</span><span className="font-medium">{aantal || "—"}</span></li>
                {prijs && !prijs.error && (
                  <>
                    <li className="flex justify-between"><span className="text-muted-foreground">Prijs p.p.</span><span className="font-medium">{euro(prijs.pp)}</span></li>
                    <li className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">Totaal (indicatief)</span><span className="font-medium">{euro(prijs.totaal)}</span></li>
                  </>
                )}
              </ul>
              <button data-testid="booking-submit" type="submit" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium rounded-full px-6 py-3.5 hover:bg-primary-hover transition-colors disabled:opacity-60">
                {loading ? "Bezig..." : <>Verstuur aanvraag <ArrowRight size={16} /></>}
              </button>
              <p className="text-xs text-muted-foreground text-center mt-3">Vrijblijvend — je ontvangt eerst een offerte. Betaling verloopt via facturering.</p>
            </div>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
